angular.module('access.Nodes')
    .constant('NOTARY_ANNOTATION_STATUS', {
        SUCCESS: '200',
        CANCEL: '400',
        ERROR: 'error'  // We'll treat everything which isn't 200 or 400 as an error
    })
    .factory('NotaryAnnotation', [
        '$q',
        'NOTARY_ANNOTATION_STATUS',
        'Session',
        ($q, NOTARY_ANNOTATION_STATUS, Session) => {
            /**
             * Due to the notary annotation page loading could take some time
             * We should wait before submitting any data to it.
             * This delay is in milliseconds
             * @type {number}
             */
            const DELAY_BEFORE_SUBMITTING = 5000;

            return {
                /**
                 * Submit file to Notary Annotation service
                 * @param {string} url Notary Annotation service URL
                 * @param {ArrayBuffer} file File content
                 * @param {object[]} signees List of signees
                 * @return {Promise<object>} Resolve with annotations, reject with an error
                 */
                submit: function (url, file, signees) {
                    const formattedUrl = _formatUrl(url);

                    return $q((resolve, reject) => {
                        try {
                            const signWindow = window.open(url);

                            window.addEventListener('message', function onMessageCallback(event) {
                                if (formattedUrl.indexOf(event.origin) > -1 && event.data != null) {
                                    window.removeEventListener('message', onMessageCallback);
                                    signWindow.close();
                                    switch (event.data.code) {
                                        case '200':
                                            resolve(_unescapeAnnotations(event.data.annotations));
                                            break;

                                        default:
                                            reject(new Error(event.data.message));
                                    }
                                }
                            });

                            setTimeout(() => {
                                try {
                                    signWindow.postMessage({
                                        file: file,
                                        language: _getNotaryLocale(Session.account.locale),
                                        signees: signees
                                    }, formattedUrl);
                                } catch (err) {
                                    reject(err);
                                }
                            }, DELAY_BEFORE_SUBMITTING);
                        } catch (error) {
                            reject(error);
                        }
                    });
                },
                /**
                 * Initiate a new Notary Annotation application
                 * @param {HTMLIFrameElement} iframe IFrame with notary app running inside
                 * @param {string} url URL of the notary app
                 */
                initNewApp: (iframe, url) => {
                    const handlers = Object.values(NOTARY_ANNOTATION_STATUS).reduce((handlersMap, status) => ({
                        ...handlersMap,
                        [status]: () => { }
                    }), {});
                    const notaryAppUrl = _formatUrl(url);

                    window.addEventListener('message', _onMessageHandler);

                    return {
                        close: () => _postMessage({ close: true }),
                        destroy: () => window.removeEventListener('message', _onMessageHandler),
                        on: (status, handler) => {
                            if (!!handlers[status]) {
                                handlers[status] = handler;
                            }
                        },
                        sendFile: (file) => _postMessage({
                            mode: 'file',
                            file: file,
                            language: _getNotaryLocale(Session.account.locale),
                            user: Session.account.email,
                            showClose: false
                        })
                    };

                    function _postMessage(message) {
                        iframe.contentWindow.postMessage(message, notaryAppUrl);
                    }

                    function _onMessageHandler({ origin, data }) {
                        if (notaryAppUrl.indexOf(origin) !== 0 || !data) {
                            return;
                        }

                        const { code: status } = data;
                        const payload = _getAnnotationPayload(status, data)

                        if (angular.isFunction(handlers[status])) {
                            handlers[status](payload);
                        } else {
                            handlers[NOTARY_ANNOTATION_STATUS.ERROR](payload);
                        }
                    }

                    function _getAnnotationPayload(status, data) {
                        switch (status) {
                            case NOTARY_ANNOTATION_STATUS.SUCCESS:
                                const { annotations = [], signees = [], form_fields = [] } = data;
                                return {
                                    annotations: _unescapeAnnotations(annotations),
                                    form_fields,
                                    signees
                                };

                            case NOTARY_ANNOTATION_STATUS.CANCEL:
                                return { message: data.message };

                            default:
                                return new Error(data.message);
                        }
                    }
                }
            };

            /**
             * In case when the URL is relative, in order to be able
             * to post message, we should prepend it with a hostname
             * to create a full URL
             * @param {string} url Input URL
             * @returns {string} Formatted URL
             */
            function _formatUrl(url) {
                return !/^http(s)?:/.test(url) ? location.protocol + '//' + location.host + url : url;
            }

            /**
             * Returns an appropriate local for the Notary service
             * @param {string} locale Files Cloud locale
             * @returns {string} Notary locale
             */
            function _getNotaryLocale(locale) {
                switch (locale) {
                    case 'zh_cn':
                        return 'zh';
                    case 'zh_tw':
                        return 'zh-TW';
                    case 'pt_pt':
                        return 'pt';
                    case 'pt_br':
                        return 'pt-BR';
                    default:
                        return locale;
                }
            }


            // Annotations can come doubly escaped from Notary's annotations page
            function _unescapeAnnotations(annotations) {
                return annotations.map(function (row) {
                    return row.replaceAll('\\"', '"');
                })
            }
        }
    ]);
